import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, Send, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import ContratacaoHistorico from '@/components/ContratacaoHistorico';
import VerificarStatusClicksign from '@/components/VerificarStatusClicksign';
import DocumentosValidacaoRH from '@/components/DocumentosValidacaoRH';

interface ContratacaoItem {
  id: string;
  email: string;
  nomeTitular?: string;
  nome_completo?: string;
  position?: string;
  cargo_id?: string;
  cargo_nome?: string; // Adicionar campo para nome do cargo
  department?: string;
  stage: string;
  notes?: string;
  salario?: number;
  jornada_trabalho?: string;
  ids_modelos_contrato?: string[];
  created_at: string;
  updated_at: string;
  cpf?: string;
  celular?: string;
  hEntrada?: string;
  hSaida?: string;
  dataInicio?: string;
  cargo?: string;
  modelo_proposta_id?: string;
  id_usuario?: string; // Adicionar campo id_usuario
  // Campos do responsável
  menor_idade?: boolean;
  tipo_responsavel?: string;
  nome_responsavel?: string;
  cpf_responsavel?: string;
  email_responsavel?: string;
  descricao_outro_responsavel?: string;
}

interface DadosPessoais {
  id: string;
  user_id: string;
  nome_completo?: string;
  nome_social?: string;
  data_nascimento?: string;
  cpf?: string;
  rg?: string;
  genero?: string;
  genero_personalizado?: string;
  celular?: string;
  estado_civil?: string;
  nacionalidade?: string;
  nivel_escolaridade?: string;
  cor_pele?: string;
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  numero_pis?: string;
  titulo_eleitor?: string;
  carteira_trabalho?: string;
  transporte_quantidade_ida?: number;
  transporte_valor_ida?: number;
  transporte_quantidade_volta?: number;
  transporte_valor_volta?: number;
  banco?: string;
  agencia?: string;
  conta?: string;
  tipo_conta?: string;
  chave_pix?: string;
  tipo_chave_pix?: string;
  // Campos de responsável legal
  tipo_responsavel?: string;
  nome_responsavel?: string;
  cpf_responsavel?: string;
  email_responsavel?: string;
  descricao_outro_responsavel?: string;
}

interface ModeloContrato {
  id: string;
  nome_modelo: string;
}

export default function ContratacaoView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const { hasPermission, refetch } = usePermissions();
  const [contratacao, setContratacao] = useState<ContratacaoItem | null>(null);
  const [dadosPessoais, setDadosPessoais] = useState<DadosPessoais | null>(null);
  const [modelos, setModelos] = useState<ModeloContrato[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionsChecked, setPermissionsChecked] = useState(false);
  const [dependentesData, setDependentesData] = useState<any[]>([]);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [restarting, setRestarting] = useState(false);

  const canRead = hasPermission('recursos_humanos', 'contratacao', 'read');
  const canWrite = hasPermission('recursos_humanos', 'contratacao', 'write');

  const handleReiniciarProcesso = async () => {
    if (!id) return;
    
    const confirmacao = window.confirm(
      'Tem certeza que deseja reiniciar este processo?\n\n' +
      'Esta ação irá:\n' +
      '- Deletar todas as propostas e contratos relacionados\n' +
      '- Resetar o status para "proposta"\n' +
      '- Manter os dados pessoais já preenchidos\n\n' +
      'Esta ação não pode ser desfeita.'
    );
    
    if (!confirmacao) return;
    
    setRestarting(true);
    try {
      const { error } = await supabase.rpc('reiniciar_contratacao', {
        contratacao_id_param: id
      });

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Processo reiniciado com sucesso!",
      });
      
      // Recarregar a página para mostrar dados atualizados
      window.location.reload();
    } catch (error: any) {
      console.error('Erro ao reiniciar processo:', error);
      toast({
        title: "Erro",
        description: error.message || "Erro ao reiniciar processo",
        variant: "destructive",
      });
    } finally {
      setRestarting(false);
    }
  };

  const handleReenviarNotificacao = async () => {
    if (!contratacao?.celular) {
      toast({
        title: "Erro",
        description: "Celular não informado para esta contratação",
        variant: "destructive",
      });
      return;
    }

    setSendingNotification(true);
    try {
      const { error } = await supabase.functions.invoke('webhook-nova-contratacao', {
        body: { celular: contratacao.celular }
      });

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Notificação reenviada com sucesso!",
      });
    } catch (error: any) {
      console.error('Erro ao reenviar notificação:', error);
      toast({
        title: "Erro",
        description: error.message || "Erro ao reenviar notificação",
        variant: "destructive",
      });
    } finally {
      setSendingNotification(false);
    }
  };

  useEffect(() => {
    refetch().then(() => {
      setPermissionsChecked(true);
    });
  }, []);

  useEffect(() => {
    if (!permissionsChecked) return;
    
    if (!canRead || !id) {
      navigate('/intranet/contratacao');
      return;
    }
    
    fetchContratacao();
    fetchModelos();
  }, [id, canRead, permissionsChecked]);

  const fetchContratacao = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      console.log('Iniciando busca da contratação para ID:', id);
      
      // Buscar dados da contratação
      const { data: contratacaoData, error: contratacaoError } = await supabase
        .from('contratacao')
        .select('*, id_usuario')
        .eq('id', id)
        .single();

      if (contratacaoError) throw contratacaoError;

      // Buscar dados do cargo separadamente se houver cargo_id
      let cargoNome = null;
      if (contratacaoData.cargo_id) {
        const { data: cargoData } = await supabase
          .from('cargos')
          .select('nome')
          .eq('id', contratacaoData.cargo_id)
          .single();
        
        cargoNome = cargoData?.nome;
      }

      setContratacao({
        ...contratacaoData,
        cargo_nome: cargoNome
      });
      console.log('Contratação carregada:', contratacaoData);

      // Buscar dados pessoais se existir user_id, email ou cpf
      let userId = contratacaoData.id_usuario;
      let dadosEncontrados = false;
      
      console.log('Tentando buscar dados pessoais...', {
        id_usuario: contratacaoData.id_usuario,
        email: contratacaoData.email,
        cpf: contratacaoData.cpf
      });

      // 1. Tentar buscar diretamente pelo user_id se existir
      if (contratacaoData.id_usuario) {
        console.log('Buscando por user_id:', contratacaoData.id_usuario);
        const { data: dadosData, error: dadosError } = await supabase
          .from('dados_pessoais')
          .select('*')
          .eq('user_id', contratacaoData.id_usuario)
          .maybeSingle();
        
        if (!dadosError && dadosData) {
          setDadosPessoais(dadosData);
          dadosEncontrados = true;
          console.log('Dados pessoais encontrados pelo user_id:', dadosData);
        }
      }

      // 2. Se não encontrou pelo user_id, tentar pelo email diretamente no auth.users
      if (!dadosEncontrados && contratacaoData.email) {
        console.log('Buscando user pelo email na tabela auth:', contratacaoData.email);
        
        // Primeiro buscar o user_id pelo email usando a função do Supabase
        const { data: userIdData } = await supabase.rpc('get_user_id_by_email', {
          user_email: contratacaoData.email
        });
        
        if (userIdData) {
          userId = userIdData;
          console.log('User ID encontrado pelo email via função:', userId);
          
          const { data: dadosData } = await supabase
            .from('dados_pessoais')
            .select('*')
            .eq('user_id', userIdData)
            .maybeSingle();
          
          if (dadosData) {
            setDadosPessoais(dadosData);
            dadosEncontrados = true;
            console.log('Dados pessoais encontrados pelo user_id do email:', dadosData);
          }
        } else {
          // Fallback: buscar na tabela profiles
          const { data: userData } = await supabase
            .from('profiles')
            .select('user_id')
            .eq('email', contratacaoData.email)
            .maybeSingle();
            
          if (userData?.user_id) {
            userId = userData.user_id;
            console.log('User ID encontrado no profiles:', userId);
            
            const { data: dadosData } = await supabase
              .from('dados_pessoais')
              .select('*')
              .eq('user_id', userData.user_id)
              .maybeSingle();
            
            if (dadosData) {
              setDadosPessoais(dadosData);
              dadosEncontrados = true;
              console.log('Dados pessoais encontrados pelo user_id do profiles:', dadosData);
            }
          }
        }
      }

      // 3. Se não encontrou pelo email, tentar pelo CPF
      if (!dadosEncontrados && contratacaoData.cpf) {
        console.log('Buscando por CPF:', contratacaoData.cpf);
        const cpfLimpo = contratacaoData.cpf.replace(/[^\d]/g, '');
        const { data: dadosPorCpf } = await supabase
          .from('dados_pessoais')
          .select('*')
          .eq('cpf', cpfLimpo)
          .maybeSingle();
          
        if (dadosPorCpf) {
          userId = dadosPorCpf.user_id;
          setDadosPessoais(dadosPorCpf);
          dadosEncontrados = true;
          console.log('Dados pessoais encontrados pelo CPF:', dadosPorCpf);
        }
      }

      // Buscar dados dos dependentes se há user_id
      if (userId) {
        console.log('Buscando dependentes para user_id:', userId);
        const { data: dependentesDataResult, error: dependentesError } = await supabase
          .from('dependentes_dados')
          .select('*')
          .eq('user_id', userId)
          .order('numero_dependente');

        if (!dependentesError && dependentesDataResult) {
          setDependentesData(dependentesDataResult);
          console.log('Dependentes encontrados:', dependentesDataResult);
        }
      }

      if (!dadosEncontrados) {
        console.log('Nenhum dado pessoal encontrado para esta contratação');
      }
      
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
      navigate('/intranet/contratacao');
    } finally {
      setLoading(false);
      // Log final para debug
      setTimeout(() => {
        console.log('Estado final dos dados pessoais após carregamento:', dadosPessoais);
      }, 1000);
    }
  };

  const fetchModelos = async () => {
    try {
      const { data, error } = await supabase
        .from('tbl_modelo_contrato')
        .select('id, nome_modelo')
        .eq('status', 'ativo');

      if (error) throw error;

      setModelos(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar modelos",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const formatCurrency = (value?: number) => {
    if (!value) return 'Não informado';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Função para formatar tipo de chave PIX
  const formatTipoChavePix = (tipo?: string) => {
    const tipos = {
      cpf: 'CPF',
      celular: 'Celular',
      email: 'E-mail',
      chave_aleatoria: 'Chave Aleatória'
    };
    return tipos[tipo as keyof typeof tipos] || 'Não informado';
  };

  // Função para formatar gênero
  const formatGenero = (genero?: string, generoPersonalizado?: string) => {
    const generos = {
      masculino: 'Masculino',
      feminino: 'Feminino',
      outro: generoPersonalizado || 'Outro'
    };
    return generos[genero as keyof typeof generos] || 'Não informado';
  };

  // Função para formatar estado civil
  const formatEstadoCivil = (estadoCivil?: string) => {
    const estados = {
      solteiro: 'Solteiro(a)',
      casado: 'Casado(a)',
      divorciado: 'Divorciado(a)',
      viuvo: 'Viúvo(a)',
      uniao_estavel: 'União Estável'
    };
    return estados[estadoCivil as keyof typeof estados] || 'Não informado';
  };

  // Função para formatar escolaridade
  const formatEscolaridade = (escolaridade?: string) => {
    const niveis = {
      fundamental_incompleto: 'Ensino Fundamental Incompleto',
      fundamental_completo: 'Ensino Fundamental Completo',
      ensino_medio_incompleto: 'Ensino Médio Incompleto',
      medio_incompleto: 'Ensino Médio Incompleto',
      ensino_medio_completo: 'Ensino Médio Completo',
      medio_completo: 'Ensino Médio Completo',
      superior_incompleto: 'Ensino Superior Incompleto',
      superior_completo: 'Ensino Superior Completo',
      pos_graduacao: 'Pós-graduação',
      mestrado: 'Mestrado',
      doutorado: 'Doutorado'
    };
    return niveis[escolaridade as keyof typeof niveis] || 'Não informado';
  };

  // Função para formatar tipo de conta
  const formatTipoConta = (tipo?: string) => {
    const tipos = {
      conta_corrente: 'Conta Corrente',
      corrente: 'Conta Corrente',
      conta_poupanca: 'Conta Poupança',
      poupanca: 'Conta Poupança'
    };
    return tipos[tipo as keyof typeof tipos] || 'Não informado';
  };

  // Função para calcular idade
  const calcularIdade = (dataNascimento?: string) => {
    if (!dataNascimento) return 'Não informado';
    
    const hoje = new Date();
    const nascimento = new Date(dataNascimento);
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const mes = hoje.getMonth() - nascimento.getMonth();
    
    if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
      idade--;
    }
    
    return `${idade} anos`;
  };

  // Função para formatar data
  const formatData = (data?: string) => {
    if (!data) return 'Não informado';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  // Função para formatar tipo de dependente
  const formatTipoDependente = (tipo?: string) => {
    const tipos = {
      conjuge: 'Cônjuge ou companheiro(a)',
      filhos: 'Filhos e enteados (até 21 anos ou até 24 se estudando)',
      pais: 'Pais, avós e bisavós (com renda limitada)',
      irmaos: 'Irmãos, netos, bisnetos (com guarda judicial)',
      menor_guarda: 'Menor pobre sob guarda',
      incapaz: 'Pessoa incapaz sob tutela ou curatela'
    };
    return tipos[tipo as keyof typeof tipos] || tipo || 'Não informado';
  };
  const formatTransporte = () => {
    const dados = dadosPessoais;
    if (!dados) return 'Não informado';

    const transporteInfo = [];
    
    if ((dados.transporte_quantidade_ida || 0) > 0) {
      transporteInfo.push(
        `Ida: ${dados.transporte_quantidade_ida}x - ${formatCurrency(dados.transporte_valor_ida || 0)}`
      );
    }
    
    if ((dados.transporte_quantidade_volta || 0) > 0) {
      transporteInfo.push(
        `Volta: ${dados.transporte_quantidade_volta}x - ${formatCurrency(dados.transporte_valor_volta || 0)}`
      );
    }

    return transporteInfo.length > 0 ? transporteInfo.join(' | ') : 'Não informado';
  };

  const getModelosNames = (ids?: string[]) => {
    if (!ids || ids.length === 0) return [];
    return ids.map(id => {
      const modelo = modelos.find(m => m.id === id);
      return modelo ? modelo.nome_modelo : 'Modelo não encontrado';
    });
  };

  const getStageLabel = (stage: string) => {
    const stages = {
      inicial: 'Inicial',
      documentacao: 'Documentação',
      aprovacao: 'Aprovação',
      contratado: 'Contratado'
    };
    return stages[stage as keyof typeof stages] || stage;
  };

  const getStageColor = (stage: string) => {
    const colors = {
      inicial: 'bg-blue-100 text-blue-800',
      documentacao: 'bg-yellow-100 text-yellow-800',
      aprovacao: 'bg-orange-100 text-orange-800',
      contratado: 'bg-green-100 text-green-800'
    };
    return colors[stage as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };


  if (!permissionsChecked || loading) {
    return <div className="flex justify-center p-8">Carregando...</div>;
  }

  if (!contratacao) {
    return <div className="flex justify-center p-8">Contratação não encontrada</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={() => navigate('/intranet/contratacao')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
            <h1 className="text-2xl font-bold">Visualizar Contratação</h1>
            <VerificarStatusClicksign onVerificacaoCompleta={() => window.location.reload()} />
        </div>
        <div className="flex gap-2">
          {canWrite && (
            <>
              <Button 
                variant="outline"
                onClick={handleReenviarNotificacao}
                disabled={sendingNotification}
              >
                {sendingNotification ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Reenviar Notificação
              </Button>
              <Button 
                variant="destructive"
                onClick={handleReiniciarProcesso}
                disabled={restarting}
              >
                {restarting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RotateCcw className="mr-2 h-4 w-4" />
                )}
                Reiniciar Processo
              </Button>
              <Button onClick={() => navigate(`/intranet/contratacao/${id}/edit`)}>
                <Edit className="mr-2 h-4 w-4" />
                Editar
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Dados Pessoais - Informações Básicas */}
        <Card>
          <CardHeader>
            <CardTitle>Dados Pessoais</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Nome Completo</label>
              <p className="text-base">{dadosPessoais?.nome_completo || contratacao.nome_completo || 'Não informado'}</p>
            </div>
            {dadosPessoais?.nome_social && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Nome Social</label>
                <p className="text-base">{dadosPessoais.nome_social}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-muted-foreground">Data de Nascimento</label>
              <p className="text-base">{formatData(dadosPessoais?.data_nascimento)} ({calcularIdade(dadosPessoais?.data_nascimento)})</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Gênero</label>
              <p className="text-base">{formatGenero(dadosPessoais?.genero, dadosPessoais?.genero_personalizado)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Estado Civil</label>
              <p className="text-base">{formatEstadoCivil(dadosPessoais?.estado_civil)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Nacionalidade</label>
              <p className="text-base">{dadosPessoais?.nacionalidade || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Nível de Escolaridade</label>
              <p className="text-base">{formatEscolaridade(dadosPessoais?.nivel_escolaridade)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Cor/Raça</label>
              <p className="text-base">{dadosPessoais?.cor_pele || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Celular</label>
              <p className="text-base">{dadosPessoais?.celular || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Email</label>
              <p className="text-base">{contratacao.email}</p>
            </div>
          </CardContent>
        </Card>

        {/* Responsável Legal (apenas se menor de idade estiver marcado na contratação) */}
        {contratacao.menor_idade && (contratacao.tipo_responsavel || contratacao.nome_responsavel) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Responsável Legal (Menor de Idade)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Tipo de Responsável</label>
                <p className="text-base">{contratacao.tipo_responsavel || 'Não informado'}</p>
              </div>
              {contratacao.tipo_responsavel === 'Outro' && contratacao.descricao_outro_responsavel && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Descrição</label>
                  <p className="text-base">{contratacao.descricao_outro_responsavel}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-muted-foreground">Nome do Responsável</label>
                <p className="text-base">{contratacao.nome_responsavel || 'Não informado'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">CPF do Responsável</label>
                <p className="text-base">{contratacao.cpf_responsavel || 'Não informado'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">E-mail do Responsável</label>
                <p className="text-base">{contratacao.email_responsavel || 'Não informado'}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Documentos */}
        <Card>
          <CardHeader>
            <CardTitle>Documentos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">CPF</label>
              <p className="text-base">{dadosPessoais?.cpf || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">RG</label>
              <p className="text-base">{dadosPessoais?.rg || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">PIS</label>
              <p className="text-base">{dadosPessoais?.numero_pis || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Título de Eleitor</label>
              <p className="text-base">{dadosPessoais?.titulo_eleitor || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Carteira de Trabalho</label>
              <p className="text-base">{dadosPessoais?.carteira_trabalho || 'Não informado'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Endereço */}
        <Card>
          <CardHeader>
            <CardTitle>Endereço</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">CEP</label>
              <p className="text-base">{dadosPessoais?.cep || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Logradouro</label>
              <p className="text-base">{dadosPessoais?.logradouro || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Número</label>
              <p className="text-base">{dadosPessoais?.numero || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Complemento</label>
              <p className="text-base">{dadosPessoais?.complemento || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Bairro</label>
              <p className="text-base">{dadosPessoais?.bairro || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Cidade</label>
              <p className="text-base">{dadosPessoais?.cidade || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Estado</label>
              <p className="text-base">{dadosPessoais?.estado || 'Não informado'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Dados Bancários */}
        <Card>
          <CardHeader>
            <CardTitle>Dados Bancários</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Banco</label>
              <p className="text-base">{dadosPessoais?.banco || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Agência</label>
              <p className="text-base">{dadosPessoais?.agencia || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Conta</label>
              <p className="text-base">{dadosPessoais?.conta || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Tipo de Conta</label>
              <p className="text-base">{formatTipoConta(dadosPessoais?.tipo_conta)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Tipo de Chave PIX</label>
              <p className="text-base">{formatTipoChavePix(dadosPessoais?.tipo_chave_pix)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Chave PIX</label>
              <p className="text-base">{dadosPessoais?.chave_pix || 'Não informado'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Transporte */}
        {((dadosPessoais?.transporte_quantidade_ida || 0) > 0 || (dadosPessoais?.transporte_quantidade_volta || 0) > 0) && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Informações de Transporte</CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Vale Transporte</label>
                <p className="text-base">{formatTransporte()}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Informações de Contratação */}
        <Card>
          <CardHeader>
            <CardTitle>Informações de Contratação</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Cargo</label>
              <p className="text-base">{(contratacao as any).cargo_nome || contratacao.cargo || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Cargo Pretendido</label>
              <p className="text-base">{contratacao.position || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Departamento</label>
              <p className="text-base">{contratacao.department || 'Não informado'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Informações Contratuais */}
        <Card>
          <CardHeader>
            <CardTitle>Informações Contratuais</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Salário</label>
              <p className="text-base">{formatCurrency(contratacao.salario)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Jornada de Trabalho</label>
              <p className="text-base">{contratacao.jornada_trabalho || 'Não informado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Estágio</label>
              <Badge className={getStageColor(contratacao.stage)}>
                {getStageLabel(contratacao.stage)}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Modelos de Contrato */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Modelos de Contrato Selecionados</CardTitle>
          </CardHeader>
          <CardContent>
            {getModelosNames(contratacao.ids_modelos_contrato).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {getModelosNames(contratacao.ids_modelos_contrato).map((nome, index) => (
                  <Badge key={index} variant="secondary">
                    {nome}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">Nenhum modelo de contrato selecionado</p>
            )}
          </CardContent>
        </Card>

        {/* Observações */}
        {contratacao.notes && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Observações</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-base whitespace-pre-wrap">{contratacao.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Informações de Sistema */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Informações de Sistema</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Criado em</label>
                <p className="text-base">
                  {new Date(contratacao.created_at).toLocaleString('pt-BR')}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Última atualização</label>
                <p className="text-base">
                  {new Date(contratacao.updated_at).toLocaleString('pt-BR')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dependentes */}
        {dependentesData.length > 0 && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Dependentes ({dependentesData.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {dependentesData.map((dependente, index) => (
                  <div key={dependente.id} className="border rounded-lg p-4 space-y-3">
                    <h4 className="font-medium text-base border-b pb-2">
                      Dependente {dependente.numero_dependente}
                    </h4>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Nome Completo</label>
                      <p className="text-base">{dependente.nome_completo}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">CPF</label>
                      <p className="text-base">{dependente.cpf_dependente}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Tipo de Dependente</label>
                      <p className="text-base">{formatTipoDependente(dependente.tipo_dependente)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Cadastrado em</label>
                      <p className="text-base text-sm">{new Date(dependente.created_at).toLocaleString('pt-BR')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Histórico de Progresso */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Histórico de Progresso</CardTitle>
          </CardHeader>
          <CardContent>
            <ContratacaoHistorico 
              contratacaoId={id!}
              userContratado={contratacao.id_usuario || dadosPessoais?.user_id || null}
            />
          </CardContent>
        </Card>

        {/* Validação de Documentos */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Validação de Documentos</CardTitle>
          </CardHeader>
          <CardContent>
            <DocumentosValidacaoRH 
              contratacaoId={id!}
              userId={dadosPessoais?.user_id || contratacao?.id_usuario}
              userEmail={contratacao?.email}
              userCpf={contratacao?.cpf || dadosPessoais?.cpf}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}